import { useEffect, useLayoutEffect, useState } from 'react';
import { BRAND, type BrandingDTO } from '@zuri/shared';
import { cx, initials } from '../lib/format';
import { brandFromBranding, installTheme, setFavicon } from '../lib/theme';

/** Tema efetivo (claro/escuro) observando o atributo aplicado no <html>. */
export function useResolvedTheme(): 'light' | 'dark' {
  const [mode, setMode] = useState<'light' | 'dark'>(() => (document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light'));
  useEffect(() => {
    const obs = new MutationObserver(() => setMode(document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light'));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  return mode;
}

/**
 * Aplica a identidade do contexto atual antes da pintura (sem piscar a marca de outra empresa).
 * branding = null → identidade neutra da plataforma.
 */
export function useBrandTheme(branding: BrandingDTO | null | undefined, title?: string) {
  useLayoutEffect(() => {
    installTheme(brandFromBranding(branding));
    setFavicon(branding?.symbolUrl ?? null);
    document.title = title ? `${title} · ${BRAND.name}` : BRAND.name;
  }, [branding?.primaryColor, branding?.accentColor, branding?.highlightColor, branding?.symbolUrl, title]); // eslint-disable-line react-hooks/exhaustive-deps
}

/** Marca da plataforma (identidade neutra). */
export function PlatformMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden>
      <rect width="64" height="64" rx="15" fill="var(--primary)" />
      <path d="M20 21h24L22 43h22" fill="none" stroke="var(--on-primary)" strokeWidth="5.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * Logotipo oficial da empresa: versão para fundo claro ou negativa conforme o tema (ou forçada por
 * `onDark`). Sem logotipo cadastrado, mostra o nome — nunca reconstrói a marca com texto estilizado.
 */
export function CompanyLogo({ branding, name, height = 28, onDark, className, variant = 'logo' }: { branding: BrandingDTO | null | undefined; name: string; height?: number; onDark?: boolean; className?: string; variant?: 'logo' | 'symbol' }) {
  const theme = useResolvedTheme();
  const dark = onDark ?? theme === 'dark';
  const src = variant === 'symbol' ? branding?.symbolUrl : dark ? branding?.logoDarkUrl ?? branding?.logoUrl : branding?.logoUrl ?? branding?.logoDarkUrl;
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  if (src && !failed) {
    return <img src={src} alt={name} height={height} style={{ height, width: 'auto', maxWidth: '100%' }} className={cx('block select-none object-contain', className)} draggable={false} onError={() => setFailed(true)} />;
  }
  if (variant === 'symbol') return <CompanyAvatar name={name} size={height} />;
  return <span className={cx('block truncate font-semibold tracking-[-0.01em]', className)} style={{ fontSize: Math.round(height * 0.56) }}>{name}</span>;
}

/** Avatar compacto: símbolo da empresa ou iniciais. */
export function CompanyAvatar({ name, src, size = 32, className }: { name: string; src?: string | null; size?: number; className?: string }) {
  const [failed, setFailed] = useState(false);
  if (src && !failed) {
    return (
      <span className={cx('grid shrink-0 place-items-center rounded-[10px] bg-surface shadow-xs', className)} style={{ width: size, height: size }}>
        <img src={src} alt="" width={size} height={size} style={{ width: size * 0.74, height: size * 0.74 }} className="object-contain" onError={() => setFailed(true)} draggable={false} />
      </span>
    );
  }
  return (
    <span className={cx('grid shrink-0 place-items-center rounded-[10px] bg-primary-soft font-semibold text-primary-text', className)} style={{ width: size, height: size, fontSize: size * 0.38 }} aria-hidden>
      {initials(name)}
    </span>
  );
}
